import { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"
import {
  TrendingUp,
  Filter,
  RefreshCw,
  CheckCircle,
  X,
  Clock,
  Zap,
  Menu,
  ChevronRight,
  PlusCircle,
  Trash2,
  Calendar,
  DollarSign,
  Search,
  ArrowUp,
  ArrowDown,
  MoreVertical,
  AlertCircle
} from "lucide-react"
import WrapUpP2p from "./layouts/WrapUpP2p"
import { exchangeActions } from "../../../redux/actions"
import { exchangeSelect } from "../../../redux/selectors"
import { formatDateTime, money, notify } from "../../../utility/Helper1"

const InvestmentListPage = () => {
  // States
  const [activeFilter, setActiveFilter] = useState("All")
  const [searchQuery, setSearchQuery] = useState("")
  const [sortBy, setSortBy] = useState("interestRate")
  const [sortOrder, setSortOrder] = useState("desc")
  const [loadingData, setLoadingData] = useState(false)
  const [showMenu, setShowMenu] = useState(false)
  const [selectedInvestment, setSelectedInvestment] = useState(null)
  const [confirmDelete, setConfirmDelete] = useState(false)

  // Redux and Router
  const exchangeData = useSelector(exchangeSelect)
  const investments = exchangeData?.filter(el => Number(el.asset) == 1 && el.utility == 0)
  const dispatch = useDispatch()
  const navigate = useNavigate()

  // Load data on component mount
  useEffect(() => {
    onLoadPH()
  }, [])

  // Functions
  const onLoadPH = () => {
    setLoadingData(true)
    dispatch(exchangeActions.onGetExchange("p2p/load/all", response => {
      setLoadingData(false)
    }))
  }

  const deletePH = (objToDelete) => {
    dispatch(exchangeActions.onGetExchange(`p2p/exchange/delete/${objToDelete?.id}`, response => {
      notify(response.msg, response.code)
      setConfirmDelete(false)
      setSelectedInvestment(null)
    }))
  }

  // Filter options
  const filterOptions = ["All", "Peered", "Reinvested", "Matured"]

  // Filter investments based on active filter and search query
  const filteredInvestments = investments?.filter((investment) => {
    // Search filter
    if (searchQuery && !investment.transid.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false
    }

    // Category filter
    if (activeFilter === "All") return true
    if (activeFilter === "Peered" && investment.matchedph > 0) return true
    if (activeFilter === "Reinvested" && investment.recommitted == 1) return true
    if (activeFilter === "Matured" && investment.phready == "NO") return true

    return false
  })

  // Sort investments
  const sortedInvestments = [...(filteredInvestments ?? [])].sort((a, b) => {
    if (sortOrder === "asc") {
      return a[sortBy] - b[sortBy]
    } else {
      return b[sortBy] - a[sortBy]
    }
  })

  // Helper functions
  const getElapsedPercentage = (phpTimestamp, days) => {
    if (!phpTimestamp || !days) return 0

    const start = phpTimestamp * 1000 // Convert from seconds to milliseconds
    const end = start + (days * 24 * 60 * 60 * 1000) // Add days
    const now = Date.now()

    if (now >= end) return 100

    const elapsed = now - start
    const total = end - start
    const percent = (elapsed / total) * 100

    return Math.min(Math.max(percent, 0), 100)
  }

  const toggleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc")
    } else {
      setSortBy(field)
      setSortOrder("desc")
    }
  }

  return (
    <WrapUpP2p>
      <div className="min-h-screen bg-black text-white">

        {/* Main Content */}
        <div className="ml-1 md:ml-20">

          {/* Mobile Search */}
          <div className="men p-4 border-b border-gray-800">
            <div className="relative">
              <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" />
              <input
                type="text"
                placeholder="Search bids..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="bg-gray-900/50 border border-gray-800 rounded-full py-2 pl-10 pr-4 text-sm focus:outline-none focus:border-cyan-500/50 w-full"
              />
            </div>
          </div>

          {/* Investment Grid */}
          <div className="p-6">
            {sortedInvestments.length > 0 ? (
              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                {sortedInvestments.map((investment) => {
                  const fundingPercentage = getElapsedPercentage(investment?.phtimestamp2, investment?.ghday)
                  const deletable = investment.lastph == investment.ph
                  const isSelected = selectedInvestment?.id === investment.id

                  return (
                    <div
                      key={investment.id}
                      className={`group relative rounded-2xl overflow-hidden transition-all duration-300 ${isSelected ? "ring-2 ring-offset-2 ring-cyan-500" : ""
                        }`}
                      onClick={() => setSelectedInvestment(isSelected ? null : investment)}
                    >
                      {/* Animated gradient border */}
                      <div className="absolute inset-0 bg-gradient-to-r from-cyan-500 via-purple-600 to-pink-500 opacity-70 blur p-[1.5px] animate-border-flow"></div>

                      <div className="absolute inset-0 rounded-2xl bg-gray-900/95 m-[1px] z-10"></div>

                      <div className="relative z-20 p-5">
                        {/* Card Header */}
                        <div className="flex justify-between items-start mb-4">
                          <div>
                            <div className="text-xs text-gray-500 mb-1">Total Bid Sum</div>
                            <div className="flex items-center">
                              <div className="w-3 h-3 rounded-full bg-gradient-to-r from-cyan-400 to-purple-600 mr-2"></div>
                              <div className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-500">
                                {money(investment.lastph)}
                              </div>
                            </div>
                            <div className="flex items-center mt-1 text-xs text-gray-400">
                              <Clock size={12} className="mr-1" />
                              {formatDateTime(Number(investment.phtimestamp2))}
                            </div>
                          </div>

                          <div className="flex items-center space-x-1 pointer">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs ${investment.recommitted == 1
                              ? "text-cyan-300 bg-cyan-900/30"
                              : "text-pink-300 bg-pink-900/30"
                              }`}>
                              <CheckCircle size={10} className="mr-1" /> View Detail
                            </span>

                            {deletable && 1>2 && (
                              <button
                                className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded-full hover:bg-gray-800"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setConfirmDelete(true);
                                }}
                              >
                                <Trash2 size={14} className="text-red-400" />
                              </button>
                            )}
                          </div>
                        </div>

                        {/* Card Content - Amount Display */}
                        <div className="flex items-center justify-between mb-6">
                          <div>
                            <div className="text-xs text-gray-500 mb-1">Sum Available for Pairing</div>

                            <div className="flex items-center">
                              <div className="w-3 h-3 rounded-full bg-gradient-to-r from-cyan-400 to-purple-600 mr-2"></div>
                              <div className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-500">
                                {money(investment.ph)}
                              </div>
                            </div>

                          </div>

                        </div>

                        {/* Card Footer - Status Chips */}
                        <div className="flex flex-wrap gap-2 mt-3">
                          <div className="px-2 py-1 bg-gray-800 rounded-md text-xs text-gray-400">
                            {Math.round((Date.now() - investment.phtimestamp2 * 1000) / (24 * 60 * 60 * 1000))} days active
                          </div>

                          {investment.matchedph > 0 && (
                            <div className="px-2 py-1 bg-purple-900/30 text-purple-300 rounded-md text-xs">
                              Peered
                            </div>
                          )}

                          {investment.phready === "NO" && (
                            <div className="px-2 py-1 bg-cyan-900/30 text-cyan-300 rounded-md text-xs">
                              Matured
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center p-10 border border-gray-800 rounded-2xl bg-gray-900/50 text-center">
                <div className="w-16 h-16 rounded-full bg-gray-800/50 flex items-center justify-center mb-4">
                  <AlertCircle size={24} className="text-gray-500" />
                </div>
                <h3 className="text-xl font-medium text-gray-400 mb-2">No Bid Found</h3>
                <p className="text-gray-500 max-w-md mb-6">
                  No Bid match your current filter criteria. Try changing your filter or create a new bid.
                </p>

                <div style={{flexDirection: 'row', flex: 1, gap: 20}}>
                  <button
                    onClick={() => setActiveFilter("All")}
                    className="px-4 py-2 rounded-lg border border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10 transition-colors"
                  >
                    View All Bids
                  </button>

                  <button
                    onClick={() => navigate('/submit/request')}
                    className="px-4 py-2 rounded-lg border border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10 transition-colors"
                  >
                    New Bids
                  </button>
                </div>


                

              </div>
            )}
          </div>
        </div>

        {/* Investment Details Drawer - Appears when an investment is selected */}
        {selectedInvestment && (
          <div className="fixed inset-0 z-30 overflow-hidden" onClick={() => setSelectedInvestment(null)}>
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm"></div>

            <div className="absolute inset-y-0 right-0 max-w-md w-full bg-gradient-to-b from-gray-900 to-black border-l border-cyan-500/20"
              onClick={(e) => e.stopPropagation()}>

              <div className="flex items-center justify-between p-6 border-b border-gray-800">
                <h2 className="text-xl font-bold text-white">Investment Details</h2>
                <button onClick={() => setSelectedInvestment(null)} className="p-2 rounded-full hover:bg-gray-800">
                  <X size={20} />
                </button>
              </div>

              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="text-lg font-mono font-bold">{selectedInvestment?.transid}</div>
                  <div className={`px-2 py-1 rounded-full text-xs ${selectedInvestment?.recommitted == 1
                    ? "text-cyan-300 bg-cyan-900/30"
                    : "text-pink-300 bg-pink-900/30"
                    }`}>
                    {selectedInvestment?.recommitted == 1 ? "Recommitted" : "Not Recommitted"}
                  </div>
                </div>

                <div className="space-y-6">
                  <div className="bg-gray-800/50 rounded-xl p-4">
                    <div className="text-sm text-gray-400 mb-1">Creation Date</div>
                    <div className="text-white">{formatDateTime(Number(selectedInvestment?.phtimestamp2))}</div>
                  </div>

                  <div className="grid grid-cols-1 gap-4">
                    <div className="bg-gray-800/50 rounded-xl p-4">
                      <div className="text-sm text-gray-400 mb-1">Total Amount</div>
                      <div className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-500">
                        {money(selectedInvestment?.lastph, selectedInvestment?.motherType)}
                      </div>
                    </div>

                  </div>

                  <div className="bg-gray-800/50 rounded-xl p-4">
                    <div className="text-sm text-gray-400 mb-1">Term Duration</div>
                    <div className="text-white">{selectedInvestment?.ghday} days</div>
                  </div>

                  <div className="bg-gray-800/50 rounded-xl p-4">
                    <div className="text-sm text-gray-400 mb-2">Maturity Date</div>
                    <div className="flex justify-between text-xs mb-2">
                      <div>Progress</div>
                      <div className="text-cyan-400">
                        {selectedInvestment?.timephconf2 == 0 ? '-' : formatDateTime(Number(selectedInvestment?.timephconf2) + (Number(selectedInvestment?.ghday) * 60*60*24))}
                      </div>
                    </div>

                    <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-cyan-500 via-purple-500 to-pink-500"
                        style={{ width: selectedInvestment?.timephconf2 == 0 ? 0 : `${getElapsedPercentage(selectedInvestment?.timephconf2, selectedInvestment?.ghday)}%` }}
                      ></div>
                    </div>
                  </div>

                  {selectedInvestment?.lastph == selectedInvestment?.ph && selectedInvestment.phready == 'YES' && (
                    <button
                      onClick={() => setConfirmDelete(true)}
                      className="w-full py-3 px-4 bg-gradient-to-r from-red-500/20 to-red-600/20 hover:from-red-500/30 hover:to-red-600/30 border border-red-500/30 rounded-xl text-red-400 flex items-center justify-center transition-colors"
                    >
                      <Trash2 size={18} className="mr-2" />
                      Delete Bid
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Confirmation Dialog */}
        {confirmDelete && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setConfirmDelete(false)}></div>

            <div className="relative w-full max-w-md bg-gradient-to-b from-gray-900 to-black border border-red-500/30 rounded-2xl overflow-hidden p-1">
              {/* Animated gradient border */}
              <div className="absolute inset-0 bg-gradient-to-r from-red-500 via-red-600 to-red-700 opacity-30 blur-sm"></div>

              <div className="relative bg-gray-900 rounded-2xl overflow-hidden">
                <div className="p-6 text-center">
                  <div className="w-16 h-16 mx-auto rounded-full bg-red-500/10 flex items-center justify-center mb-4">
                    <AlertCircle size={32} className="text-red-500" />
                  </div>

                  <h3 className="text-xl font-bold text-white mb-2">Confirm Deletion</h3>
                  <p className="text-gray-400 mb-6">
                    Are you sure you want to permanently delete this bid? This action cannot be undone.
                  </p>

                  <div className="flex justify-center space-x-4">
                    <button
                      onClick={() => setConfirmDelete(false)}
                      className="px-5 py-2 rounded-lg border border-gray-700 text-gray-300 hover:bg-gray-800 transition-colors"
                    >
                      Cancel
                    </button>

                    <button
                      onClick={() => deletePH(selectedInvestment)}
                      className="px-5 py-2 rounded-lg bg-gradient-to-r from-red-500/20 to-red-600/20 hover:from-red-500/30 hover:to-red-600/30 border border-red-500/30 text-red-400 transition-colors"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

      </div>
    </WrapUpP2p>
  )
}

export default InvestmentListPage